import { sortBy } from 'lodash'
import { Decimal } from 'decimal.js-light'
import { discountTypes } from '../dominio'

const getInitialPriceRule = () => ({
  priceTable: null,
  productKit: null,
  promotion: null,
  tieredPrice: null,
  itemReturned: null,
  priceInfo: {
    /** Valor mínimo para edição quando aplica regra de valor unitário manual */
    minimumUnitValue: 0,
    unitValue: 0,
    originalPrice: 0,
    price: 0,
    contractualFine: 0,
    promotionalPriceFrom: 0,
    hasOldPrice: false,
    oldPrice: 0,

    unitDiscount: 0,
    discountType: discountTypes.computed.discountTypesEnum().VALUE,
    unitDiscountValue: 0,

    localDiscountTotal: 0,
    localPriceTableDiscountTotal: 0,
    localGrossValue: 0,
    localNetValue: 0,
  },
})

const payBoxUtils = {
  priceRuleTypes: {
    TIERED_PRICE: 'TIERED_PRICE',
    PROMOTION: 'PROMOTION',
    PRICE_TABLE: 'PRICE_TABLE',
    KIT: 'KIT',
    KIT_ITEM: 'KIT_ITEM',
    RETURNED_ITEM: 'RETURNED_ITEM',
    MANUAL_PRICE_INCREASE: 'MANUAL_PRICE_INCREASE',
    SPEND_AND_GET_REWARD: 'SPEND_AND_GET_REWARD',
  },

  calculateItemPrice({
    discountType,
    unitDiscount,
    quantity,
    promotionId,
    priceTableId,
    productKitId,
    itemReturnedId,
    priceRules,
    forcedPriceRuleType,
  }) {
    const localQuantity = quantity || 0
    const { priceRuleSelected } = this.getPriceRule(
      {
        quantity: localQuantity,
        promotionId,
        priceTableId,
        productKitId,
        itemReturnedId,
        forcedPriceRuleType,
      },
      priceRules
    )
    const localUnitValue = priceRuleSelected.priceInfo?.unitValue

    const netValue = new Decimal(localUnitValue).mul(localQuantity)
    let totalDiscount = new Decimal(0)
    let unitDiscountValue = new Decimal(0)
    let unitPriceTableDiscountValue = new Decimal(0)
    let totalPriceTableDiscount = new Decimal(0)

    // O arredondamento dos descontos para 2 casas decimais deve ser feito sempre antes de multiplicar pela quantidade.

    if (unitDiscount > 0) {
      unitDiscountValue = unitDiscountValue.add(
        this.getDiscountValue({
          value: localUnitValue,
          discount: unitDiscount,
          discountType,
        }) || 0
      )
      totalDiscount = totalDiscount.add(
        new Decimal(unitDiscountValue).toDecimalPlaces(2).mul(localQuantity)
      )
    }

    if (this.canApplyPriceTableDiscount(priceRuleSelected)) {
      unitPriceTableDiscountValue = priceRuleSelected.priceTable.priceTableDiscountValue || 0

      totalPriceTableDiscount = totalPriceTableDiscount.add(
        new Decimal(unitPriceTableDiscountValue || 0).toDecimalPlaces(2).mul(localQuantity)
      )

      totalDiscount = totalDiscount.add(totalPriceTableDiscount)
    }

    priceRuleSelected.priceInfo = {
      ...priceRuleSelected.priceInfo,
      discountType,
      unitDiscount,
      unitDiscountValue: unitDiscountValue
        .add(unitPriceTableDiscountValue)
        .toDecimalPlaces(2)
        .toNumber(),

      price: new Decimal(priceRuleSelected.priceInfo.unitValue)
        .minus(unitPriceTableDiscountValue)
        .minus(unitDiscountValue)
        .toDecimalPlaces(2)
        .toNumber(),

      localDiscountTotal: totalDiscount.toDecimalPlaces(2).toNumber(),
      localPriceTableDiscountTotal: totalPriceTableDiscount.toDecimalPlaces(2).toNumber(),

      localGrossValue: new Decimal(localUnitValue).mul(localQuantity).toDecimalPlaces(2).toNumber(),
      localNetValue: netValue
        .minus(totalDiscount)
        .minus(priceRuleSelected.productKit?.localReturnedKitNetValue || 0)
        .toDecimalPlaces(2)
        .toNumber(),
    }

    return {
      priceRuleSelected,
    }
  },

  canApplyPriceTableDiscount(priceRuleSelected) {
    const rulesThatCanApplyPriceTableDiscount = [
      this.priceRuleTypes.PRICE_TABLE,
      this.priceRuleTypes.RETURNED_ITEM,
    ]

    const rulesThatCanApplyPriceTableDiscountForOldData = [
      // escalonado não aplica desconto, mantido para caso seja um item antigo com desconto.
      this.priceRuleTypes.TIERED_PRICE
    ]

    return (
      [...rulesThatCanApplyPriceTableDiscount, ...rulesThatCanApplyPriceTableDiscountForOldData].includes(priceRuleSelected?.type) &&
      priceRuleSelected?.priceTable?.discount > 0
    )
  },

  buildSaleItemReturnPriceRules(saleItemReturned) {
    return this.buildPriceRules({
      ...saleItemReturned,
      isPersisted: !!saleItemReturned.id,
      itemReturnedId: saleItemReturned.itemReturnedId,
      isReturnedItem: true,

      priceTable: {
        id: 1,
        discount: saleItemReturned.discountPriceTable,
      },
      shouldApplyDiscountPriceTable: saleItemReturned.discountPriceTable > 0,

      unitValue: saleItemReturned.unitValue,
      minimumUnitValue: saleItemReturned.minimumUnitValue,
      price: saleItemReturned.unitValue,
      originalPrice: saleItemReturned.unitValue,
      contractualFine: saleItemReturned.skuProduct?.contractualFine,

      quantity: saleItemReturned.quantity,
    })
  },

  buildPriceRulesFromSaleItem(saleItem, { manualUnitValue } = {}) {
    return this.buildPriceRules({
      ...saleItem,
      isPersisted: !!saleItem.id,
      unitValueType: saleItem.unitValueType,
      unitValue: saleItem.unitValue,
      minimumUnitValue: saleItem.minimumUnitValue,
      price: saleItem.unitValue,
      originalPrice:
        saleItem?.skuProduct?.originalPrice || saleItem.minimumUnitValue || saleItem.unitValue,
      priceTable: {
        ...saleItem.priceTable,
        discount: saleItem.discountPriceTable,
      },
      shouldApplyDiscountPriceTable: saleItem.discountPriceTable > 0,

      tieredPrices: saleItem.skuProduct?.tieredPrices,

      promotion: {
        id: saleItem.promotionId,
        promotionQuantityUnlimited: saleItem?.skuProduct?.promotionQuantityUnlimited,
        promotionQuantityAvailable: saleItem?.skuProduct?.promotionQuantityAvailable,
        promotionalPrice: saleItem?.skuProduct?.promotionalPrice,
        priceFrom: saleItem?.skuProduct?.priceFrom,
      },

      localReturnedKitNetValue: saleItem?.localReturnedKitNetValue,

      manualUnitValue,
    })
  },

  buildPriceRulesFromQuotationItem(quotationItem) {
    return this.buildPriceRules({
      ...quotationItem,
      isPersisted: !!quotationItem.id,
      unitValue: quotationItem.unitValue,
      price: quotationItem.unitValue,
      originalPrice: quotationItem.originalUnitValue || quotationItem.unitValue,
      priceTable: {
        ...quotationItem.priceTable,
        discount: quotationItem.discountPriceTable,
      },
      shouldApplyDiscountPriceTable: quotationItem.discountPriceTable > 0,

      tieredPrices: quotationItem.skuProduct?.tieredPrices,

      promotion: {
        id: quotationItem?.promotionId,
        promotionQuantityUnlimited: quotationItem?.skuProduct?.promotionQuantityUnlimited,
        promotionQuantityAvailable: quotationItem?.skuProduct?.promotionQuantityAvailable,
        promotionalPrice: quotationItem?.skuProduct?.promotionalPrice,
        priceFrom: quotationItem?.skuProduct?.priceFrom,
      },
    })
  },

  buildPriceRulesFromProduct(product, { manualUnitValue } = {}) {
    let unitValue = product.price
    if (product.kitItems?.length > 0) {
      unitValue = product.price
    }

    return this.buildPriceRules({
      ...product,
      shouldApplyDiscountPriceTable: product.shouldApplyDiscountPriceTable,
      promotionId: null,
      promotion: {
        id: product.promotionId,
        promotionQuantityUnlimited: product.promotionQuantityUnlimited,
        promotionQuantityAvailable: product.promotionQuantityAvailable,
        promotionalPrice: product.promotionalPrice,
        priceFrom: product.priceFrom,
      },
      unitValue,
      manualUnitValue,
      minimumUnitValue: product.price,
    })
  },

  buildPriceRules({
    isPersisted,
    unitValueType,
    unitValue,
    minimumUnitValue,
    price,
    originalPrice,
    contractualFine,

    quantity,
    unitDiscount,
    discountType,

    priceTable,
    shouldApplyDiscountPriceTable,

    productKitId,
    kitItems,

    /** id que ficou salvo no item */
    promotionId,
    /** campo com dados auxiliares (alguns são dados que não ficam salvos no item) */
    promotion,

    /** id que ficou salvo no item */
    skuTieredPriceId,
    /** campo com dados auxiliares (alguns são dados que não ficam salvos no item) */
    tieredPrices,

    manualUnitValue,

    itemReturnedId,
    isReturnedItem,
    localReturnedKitNetValue,
  }) {
    const priceRules = []

    const defaultPriceRules = {
      ...getInitialPriceRule(),
      priceTable,
      productKit: null,
      promotion: null,
      tieredPrice: null,
      itemReturned: null,
      unitValueType,
      priceInfo: {
        unitValue,
        minimumUnitValue,
        originalPrice,
        price,
        contractualFine: contractualFine || 0,
        hasOldPrice: false,
        oldPrice: originalPrice,

        unitDiscount,
        discountType,
        unitDiscountValue: 0,
      },
    }

    if (isReturnedItem) {
      const returnedDiscountValue = this.getDiscountValue({
        discount: unitDiscount,
        discountType,
        value: unitValue,
      })

      const { discountPriceTableToApply, priceTableDiscountValue, priceResult } =
        this.getPriceTableDiscountResults(priceTable, unitValue, shouldApplyDiscountPriceTable)

      priceRules.push({
        ...defaultPriceRules,
        type: this.priceRuleTypes.RETURNED_ITEM,
        priceTable: {
          ...priceTable,
          discount: discountPriceTableToApply,
          priceTableDiscountValue,
        },
        itemReturned: {
          id: itemReturnedId,
          quantityReturned: quantity,
        },
        priceInfo: {
          ...defaultPriceRules.priceInfo,
          price: priceResult,
          unitDiscountValue: returnedDiscountValue,
        },
      })

      return { priceRules }
    }

    if (kitItems?.length > 0) {
      priceRules.push({
        ...defaultPriceRules,
        type: this.priceRuleTypes.KIT,
        productKit: {
          id: productKitId,
          localReturnedKitNetValue,
        },
        priceInfo: {
          ...defaultPriceRules.priceInfo,
          originalPrice: price,
        },
      })

      return { priceRules }
    }

    if (productKitId) {
      const unitDiscountValue =
        this.getDiscountValue({
          discount: unitDiscount,
          discountType,
          value: price,
        }) || 0

      priceRules.push({
        ...defaultPriceRules,
        type: this.priceRuleTypes.KIT_ITEM,
        productKit: {
          id: productKitId,
          itemQuantity: quantity,
        },
        priceTable: {
          id: 1,
          discount: 0,
        },
        priceInfo: {
          ...defaultPriceRules.priceInfo,
          originalPrice: price,
          unitDiscountValue,
        },
      })

      return { priceRules }
    }

    if (tieredPrices?.length > 0) {
      let tieredOriginalPrice = originalPrice
      priceRules.push(
        ...tieredPrices
          .filter(tiered => tiered.id !== skuTieredPriceId)
          .map(tiered => {
            const priceResult = tiered.price

            const oldPrice = this.getOldPrice({
              price: priceResult,
              originalPrice: tieredOriginalPrice,
            })

            tieredOriginalPrice = priceResult

            return {
              ...defaultPriceRules,
              priceTable: {
                ...priceTable,
                discount: 0, // escalonado não aplica desconto da tabela de preço
              },
              type: this.priceRuleTypes.TIERED_PRICE,
              tieredPrice: tiered,
              priceInfo: {
                ...defaultPriceRules.priceInfo,
                ...oldPrice,
                minimumUnitValue: tiered.price,
                unitValue: tiered.price,
                price: priceResult,
              },
            }
          })
      )
    }

    // cenário de item que já possui preço escalonado aplicado
    if (skuTieredPriceId) {
      // escalonado não aplica desconto, mantido para caso seja um item antigo com desconto.
      const { discountPriceTableToApply, priceTableDiscountValue, priceResult } =
        this.getPriceTableDiscountResults(defaultPriceRules.priceTable, unitValue)

      const fetchedTieredPrice = tieredPrices?.find(tiered => tiered.id === skuTieredPriceId)

      const oldPrice = this.getOldPrice({
        price: priceResult,
        originalPrice,
      })

      priceRules.push({
        ...defaultPriceRules,
        type: this.priceRuleTypes.TIERED_PRICE,
        priceTable: {
          ...defaultPriceRules.priceTable,
          discount: discountPriceTableToApply,
          priceTableDiscountValue,
        },
        tieredPrice: {
          id: skuTieredPriceId,
          // preenchendo quantityFrom e quantityTo pra que quando carregue a tela selecione essa regra de preço
          quantityFrom: fetchedTieredPrice?.quantityFrom || (quantity || 0) - 1,
          quantityTo: fetchedTieredPrice?.quantityTo || (quantity || 0) + 1,
        },
        priceInfo: {
          ...defaultPriceRules.priceInfo,
          ...oldPrice,
          price: priceResult,
        },
      })
    }

    if (promotion?.id) {
      const {
        promotionQuantityUnlimited,
        promotionQuantityAvailable,
        promotionalPrice,
        priceFrom,
      } = promotion
      const promotionMaxQuantity = promotionQuantityUnlimited ? null : promotionQuantityAvailable

      const promotionPriceToApply = isPersisted ? unitValue : (promotionalPrice || unitValue)

      const oldPrice = this.getOldPrice({
        price: promotionPriceToApply,
        originalPrice,
        promotionPriceFrom: priceFrom,
      })

      priceRules.push({
        ...defaultPriceRules,
        type: this.priceRuleTypes.PROMOTION,
        priceTable: {
          ...priceTable,
          discount: 0, // garantir que não envie desconto de tabela em item promocional
        },
        promotion: {
          id: promotion?.id,
          promotionQuantityAvailable,
          promotionQuantityUnlimited,
          promotionalPrice: promotionPriceToApply,
          priceFrom,
        },
        maxQuantity: promotionMaxQuantity,
        priceInfo: {
          ...defaultPriceRules.priceInfo,
          ...oldPrice,
          minimumUnitValue: promotionPriceToApply,
          unitValue: promotionPriceToApply,
          price: promotionPriceToApply
        },
      })
    }

    if (priceTable?.id) {
      const { discountPriceTableToApply, priceTableDiscountValue, priceResult } =
        this.getPriceTableDiscountResults(priceTable, unitValue, shouldApplyDiscountPriceTable)

      const oldPrice = this.getOldPrice({
        price: priceResult,
        originalPrice,
      })

      priceRules.push({
        ...defaultPriceRules,
        type: this.priceRuleTypes.PRICE_TABLE,
        priceTable: {
          ...priceTable,
          discount: discountPriceTableToApply,
          priceTableDiscountValue,
        },
        priceInfo: {
          ...defaultPriceRules.priceInfo,
          ...oldPrice,
          minimumUnitValue: unitValue,
          unitValue,
          price: priceResult,
          unitDiscountValue: priceTableDiscountValue,
        },
      })
    }

    if (manualUnitValue > 0) {
      priceRules.push({
        ...defaultPriceRules,
        type: this.priceRuleTypes.MANUAL_PRICE_INCREASE,
        priceTable: {
          ...priceTable,
          discount: 0,
        },
        promotion: {
          id: promotionId,
        },
        tieredPrice: {
          id: skuTieredPriceId,
        },
        priceInfo: {
          ...defaultPriceRules.priceInfo,
          unitValue: manualUnitValue,
          price: manualUnitValue,
        },
      })
    }

    return { priceRules }
  },

  buildSearchedProductPrice(apiProduct) {
    const priceData = {
      price: apiProduct.price,
      ruleType: apiProduct.kit ? this.priceRuleTypes.KIT : this.priceRuleTypes.PRICE_TABLE,
    }

    const isPromotionalPrice = apiProduct.promotionId
    if (isPromotionalPrice) {
      const oldPrice = this.getOldPrice({
        price: apiProduct.price,
        originalPrice: apiProduct.originalPrice,
        promotionPriceFrom: apiProduct.priceFrom,
      })
      return {
        ...priceData,
        ...oldPrice,
        ruleType: this.priceRuleTypes.PROMOTION,
      }
    }

    const hasTieredPrices = apiProduct?.hasTieredPrices
    const firstTiered = apiProduct.tieredPrices.find(t => t.quantityFrom === 1)
    if (hasTieredPrices && firstTiered) {
      priceData.price = firstTiered?.price || apiProduct.originalPrice

      const oldPrice = this.getOldPrice({
        price: priceData.price,
        originalPrice: apiProduct.originalPrice,
      })

      return {
        ...priceData,
        ...oldPrice,
        ruleType: this.priceRuleTypes.TIERED_PRICE,
      }
    }

    if (priceData.ruleType === this.priceRuleTypes.PRICE_TABLE) {
      const { priceResult } =
        this.getPriceTableDiscountResults(apiProduct.priceTable, apiProduct.price, apiProduct.shouldApplyDiscountPriceTable)

      const oldPrice = this.getOldPrice({
        price: priceResult,
        originalPrice: apiProduct.originalPrice,
      })

      return {
        ...priceData,
        ...oldPrice,
        price: priceResult,
      }
    }

    return priceData
  },

  getOldPrice({ price, originalPrice, promotionPriceFrom }) {
    const basePrice = promotionPriceFrom || originalPrice

    return {
      hasOldPrice: price < basePrice,
      oldPrice: basePrice,
    }
  },

  getPriceTableDiscountResults(priceTable, value, shouldApplyDiscountPriceTable = false) {
    const discountPriceTableToApply = shouldApplyDiscountPriceTable ? (priceTable.discount || 0) : 0

    const priceTableDiscountValue =
      this.getDiscountValue({
        discount: discountPriceTableToApply,
        discountType: discountTypes.computed.discountTypesEnum().PERCENTAGE,
        value,
      }) || 0

    const priceResult = new Decimal(value)
      .minus(priceTableDiscountValue)
      .toDecimalPlaces(2)
      .toNumber()
    return { discountPriceTableToApply, priceTableDiscountValue, priceResult }
  },

  getPriceRule(
    { quantity, promotionId, priceTableId, productKitId, forcedPriceRuleType },
    priceRules
  ) {
    const priceRulesClone = JSON.parse(JSON.stringify(priceRules ?? []))

    if (forcedPriceRuleType) {
      if (typeof forcedPriceRuleType === 'object') {
        return {
          priceRuleSelected: forcedPriceRuleType,
        }
      }

      return {
        priceRuleSelected: priceRulesClone.find(rule => rule.type === forcedPriceRuleType),
      }
    }

    if (productKitId) {
      return {
        priceRuleSelected: priceRulesClone.find(rule => rule.productKit.id === productKitId),
      }
    }

    const tieredPriceRules = priceRulesClone.filter(
      rule => rule.type === this.priceRuleTypes.TIERED_PRICE && rule.tieredPrice?.id
    )

    const tieredPriceApplied = tieredPriceRules.find(rule => {
      const { quantityFrom, quantityTo } = rule.tieredPrice

      return quantity >= quantityFrom && (quantityTo === null || quantity <= quantityTo)
    })

    const promotionPriceRule = promotionId
      ? priceRulesClone.find(
        rule =>
          rule.type === this.priceRuleTypes.PROMOTION && rule.promotion?.id === promotionId
      )
      : null

    const priceTableRule = priceRulesClone.find(
      rule =>
        rule.type === this.priceRuleTypes.PRICE_TABLE && rule.priceTable?.id === priceTableId
    )

    const listWithPriceRules = []

    if (priceTableRule) {
      listWithPriceRules.push(priceTableRule)
    }

    if (promotionPriceRule) {
      listWithPriceRules.push(promotionPriceRule)
    }

    const canApplyTiered = !promotionPriceRule && tieredPriceApplied
    if (canApplyTiered) {
      listWithPriceRules.push(tieredPriceApplied)
    }

    const highestPriorityPrice = this.getPriceByPriority(listWithPriceRules)

    const manualPriceRule = priceRulesClone.find(
      rule => rule.type === this.priceRuleTypes.MANUAL_PRICE_INCREASE
    )
    if (manualPriceRule) {
      if (!manualPriceRule.unitValueType) {
        manualPriceRule.priceInfo.minimumUnitValue = highestPriorityPrice.priceInfo.unitValue
        manualPriceRule.tieredPrice.id = highestPriorityPrice.tieredPrice?.id
        manualPriceRule.promotion.id = highestPriorityPrice.promotion?.id
      }

      return {
        priceRuleSelected: manualPriceRule,
      }
    }

    return { priceRuleSelected: highestPriorityPrice }
  },

  getPriceByPriority(_priceRulesList) {
    const rulePriority = {
      [this.priceRuleTypes.PROMOTION]: 1,
      [this.priceRuleTypes.TIERED_PRICE]: 2,
      [this.priceRuleTypes.PRICE_TABLE]: 3,
    }

    let priceRulesList = [..._priceRulesList]
    priceRulesList = sortBy(
      priceRulesList,
      priceRule => rulePriority[priceRule.type]
    )
    return priceRulesList[0]
  },

  getDiscountValue({ discountType, discount, value }) {
    const { PERCENTAGE, VALUE } = discountTypes.computed.discountTypesEnum()
    let result = new Decimal(0)
    if (discountType === VALUE) {
      result = result.add(discount || 0)
    } else if (discountType === PERCENTAGE) {
      result = result.add(new Decimal(value).times(discount || 0).toNumber())
    }

    return result.toDecimalPlaces(2).toNumber() // roundDecimal(result, 2)
  },

  /**
   *
   * @param {Object} item
   * @param {Number} item.unitValue
   * @param {Number} item.unitDiscount
   * @param {Number} item.quantity
   * @param {String} item.discountType
   * @param {Number} item.discountPriceTable
   * @param {Number} item.priceTableId
   * @param {Number} item.promotionId
   * @param {Number} item.productKitId
   * @returns
   */
  formatBarTabConsumptionItem(item) {
    // tabela "Loja" como padrão para item sem tabela de preço
    const priceTable = { id: item.priceTableId ?? 1 }
    const { priceRules } = this.buildPriceRulesFromSaleItem({ ...item, priceTable })
    const { priceRuleSelected } = this.calculateItemPrice({
      discountType: item.discountType,
      unitDiscount: item.unitDiscount,
      quantity: item.quantity,
      promotionId: item.promotionId,
      priceTableId: priceTable.id,
      productKitId: item.productKitId,
      priceRules,
    })
    const { priceInfo } = priceRuleSelected

    return {
      ...item,
      price: priceInfo.price,
      discountTotalValue: priceInfo.localDiscountTotal,
      name: item.name || item.sku?.name || '',
    }
  },

  /**
   * Retorna os itens avulsos e, para cada kit, uma linha do kit seguida dos seus itens.
   * O item não informa a qual kit pertence, então a associação é pelo `productKitId`.
   * @param {Array} items
   * @param {Array} itemKits
   * @returns {Array}
   */
  formatBarTabConsumptionItems(items, itemKits) {
    const formattedItems = (items || []).map(item => this.formatBarTabConsumptionItem(item))
    const kitItemsByProductKitId = new Map()

    const kitRows = (itemKits || []).flatMap(itemKit => {
      const productKitId = itemKit.productKit?.id
      const itemsOfKit = kitItemsByProductKitId.has(productKitId)
        ? []
        : formattedItems.filter(item => item.productKitId === productKitId)
      kitItemsByProductKitId.set(productKitId, itemsOfKit)

      const kitRow = {
        ...itemKit,
        isKit: true,
        name: itemKit.productKit?.name || '',
        price: itemKit.unitValue,
        netValue: new Decimal(itemKit.unitValue || 0).mul(itemKit.quantity || 0).toNumber(),
      }

      return [kitRow, ...itemsOfKit.map(item => ({ ...item, isKitItem: true }))]
    })

    const looseItems = formattedItems.filter(item => !kitItemsByProductKitId.has(item.productKitId))

    return [...looseItems, ...kitRows]
  },
}

export default payBoxUtils
